import { useMemo, useState } from 'react';
import { APIProvider, AdvancedMarker, Map } from '@vis.gl/react-google-maps';
import { isLocationInDeliveryZone } from '../../../utils/deliveryZone';

export type AddressMapLocation = { lat: number; lon: number };

interface AddressMapModalProps {
  open: boolean;
  initialLocation: AddressMapLocation | null;
  addressHint: string;
  apiKey: string;
  title: string;
  subtitle: string;
  saveLabel: string;
  cancelLabel: string;
  outsideDeliveryZoneMessage: string;
  onSave: (location: AddressMapLocation) => void;
  onClose: () => void;
}

const DEFAULT_LOCATION: AddressMapLocation = { lat: 41.6423, lon: 41.6339 };

export default function AddressMapModal({
  open,
  initialLocation,
  addressHint,
  apiKey,
  title,
  subtitle,
  saveLabel,
  cancelLabel,
  outsideDeliveryZoneMessage,
  onSave,
  onClose,
}: AddressMapModalProps) {
  const [draftLocation, setDraftLocation] = useState<AddressMapLocation>(
    initialLocation ?? DEFAULT_LOCATION,
  );

  const mapCenter = useMemo(
    () => ({ lat: draftLocation.lat, lng: draftLocation.lon }),
    [draftLocation.lat, draftLocation.lon],
  );
  const isOutsideZone = !isLocationInDeliveryZone(draftLocation.lat, draftLocation.lon);

  if (!open) {
    return null;
  }

  if (!apiKey) {
    return (
      <div className="fixed inset-0 z-[120] bg-bg/90 backdrop-blur-sm p-4 flex items-center justify-center">
        <div className="w-full max-w-xl rounded-2xl bg-primary p-6 grid gap-4">
          <h3 className="m-0 text-lg font-bold text-text">{title}</h3>
          <p className="m-0 text-sm text-text-secondary">{subtitle}</p>
          <button
            type="button"
            className="w-full h-12 rounded-full bg-secondary text-text font-semibold border-none cursor-pointer"
            onClick={onClose}
          >
            {cancelLabel}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[120] bg-bg/90 backdrop-blur-sm p-3 sm:p-5 flex items-center justify-center">
      <div className="w-full max-w-3xl max-h-[95vh] rounded-2xl bg-primary overflow-hidden shadow-ambient flex flex-col">
        <div className="p-4 sm:p-5 border-b border-border grid gap-2">
          <h3 className="m-0 text-lg font-bold text-text">{title}</h3>
          <p className="m-0 text-sm text-text-secondary">{subtitle}</p>
          <p className="m-0 text-xs text-on-disabled break-words">{addressHint}</p>
        </div>
        <div className="relative h-[55vh] sm:h-[60vh]">
          <APIProvider apiKey={apiKey}>
            <Map
              defaultCenter={mapCenter}
              center={mapCenter}
              defaultZoom={16}
              onCameraChanged={(event: { detail: { center: { lat: number; lng: number } } }) => {
                const { lat, lng } = event.detail.center;
                if (Number.isFinite(lat) && Number.isFinite(lng)) {
                  setDraftLocation({ lat, lon: lng });
                }
              }}
            >
              <AdvancedMarker position={mapCenter} />
            </Map>
          </APIProvider>
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="w-8 h-8 rounded-circle border-2 border-accent bg-bg/20 backdrop-blur-sm" />
          </div>
        </div>
        {isOutsideZone ? (
          <div className="px-4 pt-4 sm:px-5">
            <p
              className="m-0 rounded-xl border border-warning/30 bg-warning-container px-4 py-3 text-sm font-medium text-on-warning-container"
              role="alert"
            >
              {outsideDeliveryZoneMessage}
            </p>
          </div>
        ) : null}
        <div className="p-4 sm:p-5 grid grid-cols-2 gap-3 border-t border-border">
          <button
            type="button"
            className="h-12 rounded-full bg-secondary text-text font-semibold border-none cursor-pointer"
            onClick={onClose}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className="h-12 rounded-full bg-accent-gradient text-on-accent font-semibold border-none cursor-pointer disabled:bg-disabled disabled:text-on-disabled disabled:cursor-not-allowed"
            onClick={() => onSave(draftLocation)}
            disabled={isOutsideZone}
          >
            {saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
